import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faColumns,
  faGripVertical,
  faRotateLeft,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import Modal from "./Modal";

const GridColumnConfigurator = ({
  isOpen,
  onClose,
  columns,
  preference,
  onSave,
  onReset,
  saving = false,
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [draggedKey, setDraggedKey] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    const byKey = new Map(columns.map((column) => [column.key, column]));
    const orderedKeys = [
      ...(preference?.columnOrder || []).filter((key) => byKey.has(key)),
      ...columns.map((column) => column.key).filter(
        (key) => !(preference?.columnOrder || []).includes(key),
      ),
    ];
    const hidden = new Set(preference?.hiddenColumns || []);
    const hasCustomPreference =
      (preference?.columnOrder || []).length > 0 ||
      (preference?.hiddenColumns || []).length > 0;
    setItems(orderedKeys.map((key) => ({
      column: byKey.get(key),
      visible: hasCustomPreference
        ? !hidden.has(key)
        : (byKey.get(key)?.showInGrid ?? true),
    })));
    setSearch("");
  }, [isOpen, columns, preference]);

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    setItems((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const dropBefore = (targetKey) => {
    if (!draggedKey || draggedKey === targetKey) return;
    setItems((current) => {
      const next = [...current];
      const sourceIndex = next.findIndex(({ column }) => column.key === draggedKey);
      const targetIndex = next.findIndex(({ column }) => column.key === targetKey);
      if (sourceIndex < 0 || targetIndex < 0) return current;
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    setDraggedKey(null);
  };

  const translatedLabel = (column) =>
    typeof column.label === "string" && column.label.includes(".")
      ? t(column.label)
      : column.label || column.key;

  const tableLabel = (column) => {
    const explicitLabel = column.tableLabel || column.sourceJoin || column.sourceTable || column.table;
    if (explicitLabel) {
      if (typeof explicitLabel === "string" && explicitLabel.includes(".")) {
        return t(explicitLabel);
      }
      if (String(explicitLabel).toLowerCase() === "salesline") {
        return t("salesLines.title");
      }
      return explicitLabel;
    }
    const key = String(column.key || "");
    const keyTable = key.includes(".") ? key.split(".")[0] : "";
    return keyTable.toLowerCase() === "salesline" ? t("salesLines.title") : keyTable;
  };

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return items.map((item, index) => ({ ...item, index }));
    }
    return items
      .map((item, index) => ({ ...item, index }))
      .filter(({ column }) => {
        const label = translatedLabel(column);
        return `${label} ${tableLabel(column)} ${column.key}`.toLowerCase().includes(needle);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, t]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("gridPreferences.title")}
      icon={faColumns}
      className="grid-column-config-modal"
    >
      <div className="grid-column-config-search">
        <FontAwesomeIcon icon={faSearch} />
        <input
          type="search"
          className="form-control"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("gridPreferences.searchPlaceholder")}
          autoComplete="off"
        />
      </div>
      <div className="grid-column-config-list">
        {filteredItems.map(({ column, visible, index }) => (
          <div
            key={column.key}
            className="grid-column-config-row"
            draggable
            onDragStart={() => setDraggedKey(column.key)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => dropBefore(column.key)}
          >
            <FontAwesomeIcon icon={faGripVertical} className="grid-column-drag-handle" />
            <input
              type="checkbox"
              checked={visible}
              onChange={(event) => {
                const checked = event.target.checked;
                setItems((current) => current.map((item) =>
                  item.column.key === column.key ? { ...item, visible: checked } : item
                ));
              }}
              aria-label={translatedLabel(column)}
            />
            <span className="grid-column-config-label">
              {tableLabel(column) && (
                <span className="grid-column-config-table">{tableLabel(column)}</span>
              )}
              <span className="grid-column-config-label-text">{translatedLabel(column)}</span>
            </span>
            <button
              type="button"
              className="btn btn-default btn-icon"
              onClick={() => move(index, -1)}
              disabled={index === 0}
              title={t("gridPreferences.moveUp")}
            >
              <FontAwesomeIcon icon={faArrowUp} />
            </button>
            <button
              type="button"
              className="btn btn-default btn-icon"
              onClick={() => move(index, 1)}
              disabled={index === items.length - 1}
              title={t("gridPreferences.moveDown")}
            >
              <FontAwesomeIcon icon={faArrowDown} />
            </button>
          </div>
        ))}
        {filteredItems.length === 0 && (
          <div className="grid-column-config-empty">
            {t("gridPreferences.noSearchResults")}
          </div>
        )}
      </div>

      <div className="modal-footer grid-column-config-actions">
        <button type="button" className="btn btn-default" onClick={onReset} disabled={saving}>
          <FontAwesomeIcon icon={faRotateLeft} /> {t("gridPreferences.restoreDefaults")}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
          {t("common.cancel")}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving || !items.some((item) => item.visible)}
          onClick={() => onSave({
            columnOrder: items.map(({ column }) => column.key),
            hiddenColumns: items.filter((item) => !item.visible).map(({ column }) => column.key),
          })}
        >
          {t("common.save")}
        </button>
      </div>
    </Modal>
  );
};

export default GridColumnConfigurator;
